import { useRef, useState, useEffect } from 'react'
import Layout from '../../components/layout/Layout'
import AnimatedChartExample from '../../components/ui/animated-chart-example'
import CarouselDots from '../../components/ui/carousel-dots'
import TransparentCard from '../../components/ui/transparent-card'
import Website3DToggle from '../../components/ui/website-3d-toggle'
import { ASSETS } from '@/constants/assets'

const ThoughtMicrocosm = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [is3DMode, setIs3DMode] = useState(false)

  // Handle page click to disable 3D mode and refresh
  const handlePageClick = () => {
    if (is3DMode) {
      setIs3DMode(false)
      setTimeout(() => {
        window.location.reload()
      }, 100)
    }
  }

  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (!container) return

    if (container.dataset.scrolling === 'true') return

    const scrollPosition = container.scrollLeft
    const cardWidth = container.offsetWidth
    const newIndex = Math.round(scrollPosition / cardWidth)
    setActiveIndex(newIndex)
  }

  const cards = [
    {
      title: "Womb",
      subtitle: "Simple solutions slices for complex problems. High Quality Knowledge in its <b>shortest form</b>"
    },
    {
      title: "Ahriman's Arrow",
      externalLink: "https://en.wikipedia.org/wiki/Ahriman",
      image: {
        src: ASSETS.images.geometricArrow,
        alt: "Geometric arrow design"
      }
    },
    {
      title: "Animated Metrics",
      customComponent: <AnimatedChartExample />
    }
  ]

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const playVideo = () => {
      video.play().catch(() => {
        // Autoplay failed, do nothing
      })
    }

    playVideo()
    video.addEventListener('ended', playVideo)
    return () => video.removeEventListener('ended', playVideo)
  }, [])

  const handleCardScroll = (index: number) => {
    const container = scrollContainerRef.current
    if (!container) return

    setActiveIndex(index)
    container.dataset.scrolling = 'true'
    
    const cardWidth = container.offsetWidth
    container.scrollTo({
      left: cardWidth * index,
      behavior: 'smooth'
    })

    setTimeout(() => {
      container.dataset.scrolling = 'false'
    }, 500)
  }

  return (
    <Layout>
      <div 
        className="flex-grow flex flex-col items-start justify-center w-full max-w-4xl px-4 md:px-8 lg:px-12"
        onClick={handlePageClick}
      >
        <div className="max-w-2xl mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-6">Microcosm</h1>
          <p className="text-foreground text-lg leading-relaxed">
            Research and Experimentation space exploring simple forms of knowledge.
          </p>
          <Website3DToggle 
            is3DMode={is3DMode} 
            setIs3DMode={setIs3DMode} 
            stopPropagation={true}
          />
        </div>
        <div 
          className="w-full overflow-x-auto pb-4 snap-x snap-mandatory" 
          ref={scrollContainerRef}
        >
          <div className="flex space-x-6 min-w-max">
            {cards.map((card, index) => (
              <div 
                key={index} 
                className="snap-center w-[calc(100vw-2rem)] max-w-[340px]"
              >
                <TransparentCard 
                  {...card}
                  className="w-full max-w-[340px] aspect-[17/20]"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16">
          <CarouselDots 
            total={cards.length} 
            active={activeIndex}
            onDotClick={handleCardScroll}
          />
        </div>
        <div className="w-full">
          <p className="text-foreground text-lg leading-relaxed mb-4">
            Key futuristic features of our AI-powered calendar app for tracking workouts could include:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground text-lg leading-relaxed">
            <li><span className="font-bold">Dynamic Schedule Adjustments</span>: AI automatically reschedules workouts based on real-time changes in your availability or energy levels.</li>
            <li><span className="font-bold">Performance-Driven Suggestions</span>: Personalized workout recommendations adapt to your fitness data and goals, updated after every session.</li>
            <li><span className="font-bold">Visual Workout Insights</span>: Interactive heatmaps and progress charts embedded into the calendar, showing workout frequency and recovery trends.</li>
            <li><span className="font-bold">Motivation Sync</span>: Integration with wearable devices to send motivational video reminders or tips when your activity levels drop.</li>
          </ul>
        </div>
        <div className="mt-12">
          <h2 className="text-[1.953rem] font-semibold text-foreground font-sans tracking-[-0.025em] leading-[1.2] [text-wrap:balance] mb-8">
            Demo Video
          </h2>
          <video 
            ref={videoRef}
            src={ASSETS.videos.cardsDemo}
            className="w-[60%] mx-auto mt-6 rounded-2xl shadow-lg block"
            autoPlay
            loop
            muted
            playsInline
          />
        </div>
      </div>
    </Layout>
  )
}

export default ThoughtMicrocosm 